import { Typography, Card, CardContent, Grid } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import DashboardHeader from "./DashboardHeader";
import SeriesDataTable from "./DataTable/DataTable";
import DashBoardSidePanel from "./DashBoardSidePanel";
import { LineChart } from "cprime-advanced-components";
import { LoadingSpinner } from "cprime-foundational-components";
import { axiosInstance } from "../../utils/axiosInstance";
import { ColumnFilter, ColumnSort } from "@tanstack/react-table";
import { formatFilterValue } from "../../utils/formatFilters";
import { getEnv } from "../../Constants/env";
import { BrowserStorage } from "../../utils/browserStorage";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  fetchApplicationDomainData,
  resetStatus,
} from "../../store/slices/applicationDomainSlice";
import { triggerToast } from "../../utils/toast";

interface DashboardProps {
  domainName: string;
  attributes: string[];
}

interface DashboardData {
  overall: {
    appsOnboarded: number;
    uniqueUsers: number;
  };
  appDetails: {
    columns: Array<{ name: string; id: string; type: string }>;
    totalCount: number;
    rows: Array<Record<string, any>>;
  };
  graphData: {
    label: string;
    data: Array<{ month: string; count: number }>;
  };
}

type DashboardLevel = "appLevel" | "roleLevel" | "featureLevel";

interface DashboardFilterValue {
  title: string;
  value: string;
  id: number;
}

interface ShareResolveData {
  level?: "app" | "role" | "feature" | string;
  valueStreamIds?: number[];
  appIds?: number[];
  sortingFilter?: {
    filter?: any[];
    sort?: Array<{ columnName: string; type: string }>;
    page?: { limit?: number; offset?: number };
  };
  [key: string]: any;
}

interface SavedViewState {
  level?: "app" | "role" | "feature" | string;
  valueStreamIds?: number[];
  appIds?: number[];
  sortingFilter?: {
    filter?: any[];
    sort?: Array<{
      columnName?: string;
      type?: string;
      field?: string;
      dir?: string;
    }>;
    page?: { limit?: number; offset?: number };
  };
  [key: string]: any;
}

type ApiInlineFilter = Record<string, any>;

const mapApiFiltersToTableFilters = (
  apiFilters: ApiInlineFilter[] = [],
): ColumnFilter[] => {
  return apiFilters
    .map((filterObj) => {
      if (!filterObj || typeof filterObj !== "object") {
        return null;
      }

      const [operator, entries] = Object.entries(filterObj)[0] ?? [];
      const firstEntry = Array.isArray(entries) ? entries[0] : undefined;
      const columnName = firstEntry?.columnName;
      const rawValue = firstEntry?.value;

      if (!operator || !columnName) {
        return null;
      }

      const valueArray = Array.isArray(rawValue)
        ? rawValue.map((v) => String(v).trim())
        : [String(rawValue ?? "").trim()];
      const joinedValues = valueArray.join(",");

      let value = "";
      switch (operator) {
        case "eq":
          value = `=${joinedValues}`;
          break;
        case "neq":
          value = `!=${joinedValues}`;
          break;
        case "gte":
          value = `>=${joinedValues}`;
          break;
        case "lte":
          value = `<=${joinedValues}`;
          break;
        case "gt":
          value = `>${joinedValues}`;
          break;
        case "lt":
          value = `<${joinedValues}`;
          break;
        case "notIn":
          value = `!${joinedValues}`;
          break;
        case "in":
          value = joinedValues;
          break;
        case "like":
          value = String(rawValue ?? "");
          break;
        default:
          return null;
      }

      return {
        id: String(columnName),
        value,
      } as ColumnFilter;
    })
    .filter((item): item is ColumnFilter => Boolean(item));
};

const Dashboard: React.FC<DashboardProps> = ({ domainName, attributes }) => {
  const initialLinkId = new URLSearchParams(window.location.search).get(
    "linkId",
  );

  const dispatch = useAppDispatch();
  const appDomainState = useAppSelector(
    (state: any) => state.applicationDomain,
  );
  const appDomainData = appDomainState?.data ?? [];

  const [isSidebarExpand, setIsSidebarExpand] = useState<boolean>(true);
  const [selectedLevel, setSelectedLevel] =
    useState<DashboardLevel>("appLevel");
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [filterValues, setFilterValues] = useState<DashboardFilterValue[]>([]);
  const [filters, setFilters] = useState<ColumnFilter[]>([]);
  const [sorting, setSorting] = useState<ColumnSort[]>([]);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 25 });
  const [savedFilterOverride, setSavedFilterOverride] = useState<any[] | null>(
    null,
  );
  const [isResolvingShareLink, setIsResolvingShareLink] = useState(
    Boolean(initialLinkId),
  );
  const [isSavedViewInitResolved, setIsSavedViewInitResolved] = useState(
    Boolean(initialLinkId),
  );
  const [resolvedFilterIds, setResolvedFilterIds] = useState<number[] | null>(
    null,
  );
  const [resolvedFilterLevel, setResolvedFilterLevel] =
    useState<DashboardLevel | null>(null);
  const isApplyingResolvedStateRef = useRef(false);
  const lastDashboardRequestKeyRef = useRef<string>("");
  const prevAppDomainEnvRef = useRef<string | null>(null);

  const getDashboardEnv = () => {
    // 1. URL ?env= param takes highest priority (shared link sets this)
    const urlEnv = new URLSearchParams(window.location.search).get("env");
    if (urlEnv) {
      return urlEnv.toUpperCase();
    }
    // 2. User-selected env from session storage (set by SitLoginScreen)
    const sessionEnv = BrowserStorage.getSessionItem("env");
    if (sessionEnv) {
      return sessionEnv.toUpperCase();
    }
    // 3. Fallback to deployment env
    const rawEnv = (getEnv()?.envtype || "dev").toLowerCase();
    const normalizedEnv = rawEnv === "local" ? "dev" : rawEnv;
    return normalizedEnv.toUpperCase();
  };

  const dashboardEnv = getDashboardEnv();

  const SIDEBAR_EXPANDED_WIDTH = 320;
  const SIDEBAR_COLLAPSED_WIDTH = 72;
  const sidebarWidth = isSidebarExpand
    ? SIDEBAR_EXPANDED_WIDTH
    : SIDEBAR_COLLAPSED_WIDTH;

  useEffect(() => {
    // Fetch app_domain only once per unique env. Guard against the brief
    // render where dashboardEnv may resolve to a stale value before the
    // URL/session env settles, which caused a duplicate call (e.g. QA then DEV).
    if (prevAppDomainEnvRef.current === dashboardEnv) {
      return;
    }
    prevAppDomainEnvRef.current = dashboardEnv;
    dispatch(resetStatus());
    dispatch(fetchApplicationDomainData(dashboardEnv));
  }, [dashboardEnv, dispatch]);

  const mapShareLevelToDashboardLevel = (
    level?: string,
  ): DashboardLevel | null => {
    if (level === "app") {
      return "appLevel";
    }
    if (level === "role") {
      return "roleLevel";
    }
    if (level === "feature") {
      return "featureLevel";
    }
    return null;
  };

  const buildFilterChips = (
    level: DashboardLevel,
    ids: number[],
  ): DashboardFilterValue[] => {
    if (ids.length === 0) {
      return [];
    }

    if (level === "appLevel") {
      return ids.map((id) => {
        const match = appDomainData.find(
          (valueStream: any) => valueStream.value_stream_id === id,
        );
        return {
          title: "VALUE STREAM",
          value: match?.value_stream_name ?? String(id),
          id,
        };
      });
    }

    const allApps = appDomainData.flatMap(
      (valueStream: any) => valueStream.applications ?? [],
    );

    return ids.map((id) => {
      const match = allApps.find((app: any) => app.app_id === id);
      return {
        title: "APP",
        value: match?.app_name ?? String(id),
        id,
      };
    });
  };

  const applySavedViewState = (
    savedState: SavedViewState,
    applyViewData: boolean = true,
  ) => {
    const mappedLevel = mapShareLevelToDashboardLevel(savedState.level);
    const nextLevel = mappedLevel ?? selectedLevel;
    const ids =
      nextLevel === "appLevel"
        ? (savedState.valueStreamIds ?? [])
        : (savedState.appIds ?? []);

    const restoredSorting = (savedState.sortingFilter?.sort ?? []).map(
      (item) => ({
        id: item.columnName ?? item.field ?? "",
        desc: String(item.type ?? item.dir ?? "")
          .toUpperCase()
          .includes("DESC"),
      }),
    );

    const limit = savedState.sortingFilter?.page?.limit;
    const offset = savedState.sortingFilter?.page?.offset;
    const safeRowsPerPage = typeof limit === "number" && limit > 0 ? limit : 25;
    const safeOffset = typeof offset === "number" && offset >= 0 ? offset : 0;

    isApplyingResolvedStateRef.current = applyViewData;
    lastDashboardRequestKeyRef.current = "";
    if (mappedLevel) {
      setSelectedLevel(mappedLevel);
    }
    setResolvedFilterIds(ids);
    setResolvedFilterLevel(nextLevel);
    setFilterValues(buildFilterChips(nextLevel, ids));
    const restoredApiFilters = savedState.sortingFilter?.filter ?? [];
    setFilters(mapApiFiltersToTableFilters(restoredApiFilters));
    setSavedFilterOverride(restoredApiFilters);
    setSorting(restoredSorting.filter((item) => item.id));
    setPagination({
      rowsPerPage: safeRowsPerPage,
      page: Math.floor(safeOffset / safeRowsPerPage),
    });
  };

  useEffect(() => {
    if (!initialLinkId) {
      return;
    }

    const resolveSharedLink = async () => {
      try {
        setIsResolvingShareLink(true);
        lastDashboardRequestKeyRef.current = "";
        const response = await axiosInstance.post(
          "/onboard/dashboard/share/resolve",
          { linkId: initialLinkId },
          { params: { env: dashboardEnv } },
        );

        const resolvedData: ShareResolveData | undefined = response?.data?.data;
        if (!resolvedData) {
          return;
        }
        applySavedViewState(resolvedData);
      } catch (error) {
        const statusCode = (error as any)?.response?.status;
        if (statusCode === 404) {
          // Fallback to the default dashboard request payload when shared link is not found.
          setSelectedLevel("appLevel");
          setFilterValues([]);
          setFilters([]);
          setSorting([]);
          setPagination({ page: 0, rowsPerPage: 25 });
          lastDashboardRequestKeyRef.current = "";
          triggerToast(
            "Shared link not found or has expired. Loaded default dashboard view.",
            "error",
          );
        }
        console.error("Error resolving shared dashboard link:", error);
      } finally {
        setIsResolvingShareLink(false);
      }
    };

    resolveSharedLink();
  }, [initialLinkId, dashboardEnv]);

  useEffect(() => {
    if (!resolvedFilterIds || !resolvedFilterLevel) {
      return;
    }
    if (appDomainData.length === 0) {
      return;
    }
    setFilterValues(buildFilterChips(resolvedFilterLevel, resolvedFilterIds));
  }, [appDomainData, resolvedFilterIds, resolvedFilterLevel]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const requestPayload: {
        valueStreamIds?: number[];
        appIds?: number[];
        sortingFilter: {
          filter: any[];
          sort: any[];
          page: { limit: number; offset: number };
        };
      } = {
        sortingFilter: {
          filter:
            filters?.length > 0
              ? filters.map((item: any) =>
                  formatFilterValue(item.value, item.id),
                )
              : (savedFilterOverride ?? []),
          sort: sorting.map((item: any) => {
            return {
              columnName: item.id,
              type: item.desc ? "DESC" : "ASC",
            };
          }),
          page: {
            limit: pagination.rowsPerPage,
            offset: pagination?.page * pagination?.rowsPerPage,
          },
        },
      };

      if (selectedLevel === "appLevel") {
        const valueStreamIds = filterValues.map((filter) => filter.id);
        if (valueStreamIds.length > 0) {
          requestPayload.valueStreamIds = valueStreamIds;
        }
      } else {
        const appIds = filterValues.map((filter) => filter.id);
        if (appIds.length > 0) {
          requestPayload.appIds = appIds;
        }
      }

      const requestUrl = `onboard/dashboard?level=${selectedLevel}`;
      const requestKey = JSON.stringify({
        url: requestUrl,
        env: dashboardEnv,
        payload: requestPayload,
      });

      if (lastDashboardRequestKeyRef.current === requestKey) {
        setLoading(false);
        return;
      }

      lastDashboardRequestKeyRef.current = requestKey;

      const response = await axiosInstance.post(requestUrl, requestPayload, {
        params: { env: dashboardEnv },
      });

      if (response?.data?.data) {
        const data = response.data.data;
        data.appDetails = data.appDetails || {
          columns: data?.columns || [],
          totalCount: data?.totalCount || 0,
          rows: data?.rows || [],
        };
        setDashboardData(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableFiltersChange = (nextFilters: ColumnFilter[]) => {
    // User-driven table filter edits (including clear) should override
    // any restored saved-view filter payload.
    setSavedFilterOverride(null);
    setFilters(nextFilters);
  };

  // Reset filter values when selected level changes
  useEffect(() => {
    if (isApplyingResolvedStateRef.current) {
      isApplyingResolvedStateRef.current = false;
      return;
    }

    if (selectedLevel) {
      setFilterValues([]);
      setFilters([]);
      setSorting([]);
      setPagination({ page: 0, rowsPerPage: 25 });
      setSavedFilterOverride(null);
      // fetchDashboardData();
    }
  }, [selectedLevel]);

  // Fetch dashboard data when dashboard level changes.
  useEffect(() => {
    if (isResolvingShareLink) {
      return;
    }

    if (!initialLinkId && !isSavedViewInitResolved) {
      return;
    }

    fetchDashboardData();
  }, [
    filterValues,
    filters,
    sorting,
    pagination,
    isResolvingShareLink,
    isSavedViewInitResolved,
    initialLinkId,
    dashboardEnv,
  ]);

  // Transform graph data for LineChart
  const chartData = dashboardData
    ? {
        datasets: [
          {
            data: dashboardData.graphData.data.map((item) => item.count),
            label: dashboardData.graphData.label,
          } as any,
        ],
        labels: dashboardData.graphData.data.map((item) => item.month),
      }
    : {
        datasets: [
          {
            data: [40, 55, 50, 50, 62, 60, 70, 66, 71, 74, 61, 66],
            label: "Applications Onboarded",
          } as any,
        ],
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
      };

  const onToggleExpand = () => {
    setIsSidebarExpand((prev) => !prev);
  };

  const shareLevelMap: Record<DashboardLevel, string> = {
    appLevel: "app",
    roleLevel: "role",
    featureLevel: "feature",
  };

  const sharePayload = {
    level: shareLevelMap[selectedLevel],
    ...(selectedLevel === "appLevel"
      ? { valueStreamIds: filterValues.map((filter) => filter.id) }
      : { appIds: filterValues.map((filter) => filter.id) }),
    sortingFilter: {
      filter:
        filters?.length > 0
          ? filters.map((item: any) => formatFilterValue(item.value, item.id))
          : [],
      sort: sorting.map((item: any) => ({
        columnName: item.id,
        type: item.desc ? "DESC" : "ASC",
      })),
      page: {
        limit: pagination.rowsPerPage,
        offset: pagination?.page * pagination?.rowsPerPage,
      },
    },
  };

  return (
    <>
      <div className="dashboard-control">
        {/* Sidebar */}
        <div
          style={{
            width: `${sidebarWidth}px`,
            minWidth: `${sidebarWidth}px`,
            maxWidth: `${sidebarWidth}px`,
            background: "rgb(255, 255, 255)",
            position: "fixed",
            height: "100vh",
            overflowX: "hidden",
            overflowY: "hidden",
            transition: "width 0.3s ease",
          }}
        >
          <DashBoardSidePanel
            isPanelOpen={isSidebarExpand}
            selectedItem={selectedLevel}
            onItemClick={(itemId) => {
              if (
                itemId === "appLevel" ||
                itemId === "roleLevel" ||
                itemId === "featureLevel"
              ) {
                setSelectedLevel(itemId);
              }
            }}
            onPanelToggle={onToggleExpand}
          />
        </div>

        {/* Main content */}
        <div
          style={{
            marginLeft: `${sidebarWidth}px`,
            padding: "16px",
            overflowY: "auto",
            transition: "margin-left 0.3s ease",
            overflowX: "hidden",
            height: "100%",
            minWidth: 0,
            position: "relative",
          }}
        >
          {loading ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                background: "rgba(248, 248, 249, 0.55)",
              }}
            >
              <LoadingSpinner />
            </div>
          ) : null}

          <div
            className="mainHeader"
            style={{
              // position: "sticky",
              // top: 0,
              zIndex: 998,
              width: "100%",
              transition: "margin-top 0.3s ease",
              marginBottom: "16px",
            }}
          >
            <DashboardHeader
              selectedLevel={selectedLevel}
              filterValues={filterValues}
              setFilterValues={setFilterValues}
              sharePayload={sharePayload}
              shareEnv={dashboardEnv}
              onApplySavedView={applySavedViewState}
              hasSharedLink={Boolean(initialLinkId)}
              onSavedViewInitResolved={() => setIsSavedViewInitResolved(true)}
            />
          </div>

          {/* Metric Cards Grid */}
          {selectedLevel === "appLevel" ? (
            <Grid container spacing={2} style={{ marginBottom: "24px" }}>
              {/* Apps Onboarded Card */}
              <Grid item xs={12} sm={6} md={6}>
                <Card
                  style={{
                    borderRadius: "4px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Applications Onboarded
                    </Typography>
                    <Typography variant="h4" style={{ fontWeight: "600" }}>
                      {loading
                        ? "-"
                        : dashboardData?.overall?.appsOnboarded || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Unique Users Card */}
              <Grid item xs={12} sm={6} md={6}>
                <Card
                  style={{
                    borderRadius: "4px",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Unique Users
                    </Typography>
                    <Typography variant="h4" style={{ fontWeight: "600" }}>
                      {loading ? "-" : dashboardData?.overall?.uniqueUsers || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : null}

          {/* Line Chart */}
          {selectedLevel === "appLevel" ? (
            <div
              style={{
                background: "#fff",
                borderRadius: "4px",
                padding: "16px",
                marginBottom: "24px",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                variant="h6"
                style={{ marginBottom: "16px", fontWeight: "600" }}
              >
                Applications Onboarded Per Month
              </Typography>
              <LineChart
                data={chartData}
                // onChartClick={() => console.info("Chart clicked!")}
                showLegend
                title=""
                xTitle="Month"
                yTitle="Count"
              />
            </div>
          ) : null}

          {/* Application Summary Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "4px",
              padding: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <SeriesDataTable
              appDetails={dashboardData?.appDetails}
              isLoading={loading}
              filters={filters}
              setFilters={handleTableFiltersChange}
              sorting={sorting}
              setSorting={setSorting}
              pagination={pagination}
              setPagination={setPagination}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
